import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import MultiselectComponent from "../../../components/MultiselectComponent";
import { useUserContext } from "../provider/UserContext";
import { buttonSpinner } from "../../../assets/icons";
import { getProjectsData } from "../../../api/mappingServices";
import { toast } from "react-toastify";
import {
  X,
  Trash2,
  Plus,
  User,
  Mail,
  Phone,
  Briefcase,
  Shield,
  FolderOpen,
  UserX,
} from "lucide-react";

const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup.string(),
  designation: yup.string().required("Designation is required"),
  permissions: yup.array().default([]),
  projectsAssigned: yup.array().of(
    yup.object().shape({
      project: yup.string().required(),
      accessControls: yup.object().shape({
        create: yup.boolean(),
        deleteOwn: yup.boolean(),
        deleteAny: yup.boolean(),
        execute: yup.boolean(),
      }),
    }),
  ),
});

const permissionsList = ["Template Manager", "Onboarding Manager"];

const UserForm = () => {
  const {
    selectedOption,
    setselectedOption,
    selectedUser,
    setSelectedUser,
    saveUser,
    loading,
  } = useUserContext();

  const [projects, setprojects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [availableProjects, setAvailableProjects] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      designation: "",
      permissions: [],
      projectsAssigned: [],
    },
  });

  useEffect(() => {
    const newDefaults = {
      firstName: selectedUser?.firstName || "",
      lastName: selectedUser?.lastName || "",
      email: selectedUser?.email || "",
      phoneNumber: selectedUser?.phoneNumber || "",
      designation: selectedUser?.designation || "",
      permissions: selectedUser?.permissions || [],
      projectsAssigned: selectedUser?.projectsAssigned || [],
    };

    reset(newDefaults);
  }, [selectedUser, selectedOption, reset]);

  useEffect(() => {
    if (projects.length === 0) fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await getProjectsData();
      setprojects(response.data);
    } catch (error) {
      toast.error("Failed to fetch projects. Please try again later.");
    } finally {
      setLoadingProjects(false);
    }
  };

  const onSubmit = (data) => {
    saveUser(data);
  };

  // Watch assigned projects
  const projectsAssigned = watch("projectsAssigned");

  // Update available projects based on already assigned ones
  useEffect(() => {
    const assignedProjectIds = projectsAssigned?.map((p) => p.project) || [];
    console.log(
      "🚀 ~ :123 ~ UserForm ~ assignedProjectIds:",
      assignedProjectIds,
    );
    const available = projects.filter(
      (p) => !assignedProjectIds.includes(p._id),
    );
    console.log("🚀 ~ :130 ~ UserForm ~ available:", available);
    setAvailableProjects(available);
  }, [projects, projectsAssigned]);

  const addProject = (projectId) => {
    const currentProjects = projectsAssigned || [];
    const newProject = {
      project: projectId,
      accessControls: {
        create: false,
        deleteOwn: false,
        deleteAny: false,
        execute: true,
      },
    };
    setValue("projectsAssigned", [...currentProjects, newProject]);
  };

  const removeProject = (index) => {
    const currentProjects = projectsAssigned || [];
    const updatedProjects = currentProjects.filter((_, i) => i !== index);
    setValue("projectsAssigned", updatedProjects);
  };

  const getPermissionColor = (permission) => {
    switch (permission) {
      case "create":
        return "text-green-600 bg-green-50";
      case "deleteOwn":
        return "text-orange-600 bg-orange-50";
      case "deleteAny":
        return "text-red-600 bg-red-50";
      case "execute":
        return "text-blue-600 bg-blue-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case "create":
        return <Plus size={14} />;
      case "deleteOwn":
        return <UserX size={14} />;
      case "deleteAny":
        return <Trash2 size={14} />;
      case "execute":
        return <FolderOpen size={14} />;
      default:
        return null;
    }
  };

  const getPermissionLabel = (permission) => {
    switch (permission) {
      case "create":
        return "Create";
      case "deleteOwn":
        return "Delete Own";
      case "deleteAny":
        return "Delete Any";
      case "execute":
        return "Execute";
      default:
        return permission;
    }
  };

  return (
    <Dialog
      open={!!selectedOption}
      as="div"
      className="relative z-50 focus:outline-none"
      onClose={() => setselectedOption(null)}
    >
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto bg-black bg-opacity-70 font-raleway">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-4xl rounded-2xl bg-white backdrop-blur-2xl shadow-2xl">
            <div className="relative">
              <DialogTitle
                as="h3"
                className="text-xl font-bold text-gray-800 border-b border-gray-200 p-6 pr-16"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="text-blue-600" size={20} />
                  </div>
                  {selectedOption === "register"
                    ? "Register New User"
                    : "Update User"}
                </div>
              </DialogTitle>
              <button
                type="button"
                onClick={() => {
                  setSelectedUser(null);
                  setselectedOption(null);
                }}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close"
                disabled={loading === "addUpdateUser"}
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>

            {loadingProjects ? (
              <div className="flex items-center justify-center py-16">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-lg">Loading projects...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-8">
                {/* Basic Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <User size={18} className="text-blue-600" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        {...register("firstName")}
                        type="text"
                        placeholder="Enter first name"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {errors.firstName && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <X size={14} />
                          {errors.firstName?.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        {...register("lastName")}
                        type="text"
                        placeholder="Enter last name"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {errors.lastName && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <X size={14} />
                          {errors.lastName?.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Mail size={16} />
                        Email Address
                      </label>
                      <input
                        {...register("email")}
                        type="email"
                        placeholder="Enter email address"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <X size={14} />
                          {errors.email?.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Phone size={16} />
                        Phone Number
                      </label>
                      <input
                        {...register("phoneNumber")}
                        type="text"
                        placeholder="Enter phone number"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Briefcase size={16} />
                        Designation
                      </label>
                      <input
                        {...register("designation")}
                        type="text"
                        placeholder="Enter designation"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      {errors.designation && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <X size={14} />
                          {errors.designation?.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Permissions Section */}
                {selectedUser?.role !== "super_admin" && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Shield size={18} className="text-purple-600" />
                      Permissions
                    </h4>
                    <MultiselectComponent
                      options={permissionsList.map((p) => ({
                        label: p,
                        value: p,
                      }))}
                      selectedOptions={watch("permissions")?.map((value) => ({
                        label: value,
                        value: value,
                      }))}
                      handleSetSelect={(selected) =>
                        setValue(
                          "permissions",
                          selected.map((s) => s.value),
                        )
                      }
                    />
                  </div>
                )}

                {/* Project Assignments Section */}
                {selectedUser?.role !== "super_admin" && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <FolderOpen size={18} className="text-green-600" />
                      Project Assignments
                    </h4>

                    {/* Add New Project Dropdown */}
                    {availableProjects.length > 0 && (
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Add Project
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              addProject(e.target.value);
                              e.target.value = "";
                            }
                          }}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        >
                          <option value="">
                            Select a project to assign...
                          </option>
                          {availableProjects.map((project) => (
                            <option key={project._id} value={project._id}>
                              {project.projectName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Assigned Projects List */}
                    {projectsAssigned && projectsAssigned.length > 0 ? (
                      <div className="space-y-4">
                        <h5 className="font-medium text-gray-700">
                          Assigned Projects ({projectsAssigned.length})
                        </h5>
                        {projectsAssigned.map((pa, index) => {
                          const project = projects.find(
                            (proj) => proj._id === pa.project,
                          );
                          const projectName =
                            project?.projectName || pa.project;

                          return (
                            <div
                              key={index}
                              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <h6 className="font-semibold text-gray-800 flex items-center gap-2">
                                  <FolderOpen
                                    size={16}
                                    className="text-green-600"
                                  />
                                  {projectName}
                                </h6>
                                <button
                                  type="button"
                                  onClick={() => removeProject(index)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove project"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>

                              <div className="flex flex-wrap gap-3">
                                {[
                                  "create",
                                  "deleteOwn",
                                  "deleteAny",
                                  "execute",
                                ].map((perm) => (
                                  <Controller
                                    key={perm}
                                    name={`projectsAssigned.${index}.accessControls.${perm}`}
                                    control={control}
                                    render={({ field }) => (
                                      <label
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                                          field.value
                                            ? getPermissionColor(perm) +
                                              " border-current"
                                            : "text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100"
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={field.value}
                                          onChange={(e) =>
                                            field.onChange(e.target.checked)
                                          }
                                          className="sr-only"
                                        />
                                        <div
                                          className={`flex items-center gap-1 ${
                                            field.value ? "font-medium" : ""
                                          }`}
                                        >
                                          {getPermissionIcon(perm)}
                                          <span>
                                            {getPermissionLabel(perm)}
                                          </span>
                                        </div>
                                        {field.value && (
                                          <div className="w-2 h-2 bg-current rounded-full"></div>
                                        )}
                                      </label>
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FolderOpen
                          size={48}
                          className="mx-auto mb-3 text-gray-300"
                        />
                        <p>No projects assigned yet</p>
                        <p className="text-sm">
                          Use the dropdown above to assign projects
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200">
                  <button
                    type="submit"
                    disabled={loading === "addUpdateUser"}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all inline-flex items-center gap-2 text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading === "addUpdateUser" ? (
                      <>
                        {buttonSpinner}
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        {selectedOption === "register" ? (
                          <>
                            <Plus size={16} />
                            Register User
                          </>
                        ) : (
                          <>
                            <User size={16} />
                            Update User
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default UserForm;
